import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    const token = localStorage.getItem('biounica_token');
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const resp = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser({ ...resp.data, token });
    } catch {
      localStorage.removeItem('biounica_token');
      localStorage.removeItem('biounica_refresh');
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const resp = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, refresh_token, user: userData } = resp.data;
    localStorage.setItem('biounica_token', access_token);
    localStorage.setItem('biounica_refresh', refresh_token);
    setUser({ ...userData, token: access_token });
    return userData;
  };

  // "next" è la pagina annunciata dal link: torna nel messaggio di accesso
  // così chi lo apre atterra lì e non in dashboard.
  const requestMagicLink = async (email, next = null) => {
    const resp = await axios.post(`${API}/auth/magic-link`, { email, next });
    return resp.data;
  };

  const confirmMagicLink = async (token) => {
    const resp = await axios.post(`${API}/auth/magic-link/confirm`, { token });
    const { access_token, refresh_token, user: userData } = resp.data;
    localStorage.setItem('biounica_token', access_token);
    localStorage.setItem('biounica_refresh', refresh_token);
    setUser({ ...userData, token: access_token });
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('biounica_token');
    localStorage.removeItem('biounica_refresh');
    setUser(null);
  };

  const resetPassword = async (email) => {
    const resp = await axios.post(`${API}/auth/reset-password`, { email });
    return resp.data;
  };

  const updateProfile = async (data) => {
    const token = localStorage.getItem('biounica_token');
    const resp = await axios.patch(`${API}/profile`, data, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUser((prev) => ({
      ...prev,
      profile: { ...prev.profile, ...resp.data },
    }));
    return resp.data;
  };

  const changePassword = async (newPassword) => {
    const token = localStorage.getItem('biounica_token');
    const resp = await axios.post(
      `${API}/auth/change-password`,
      { new_password: newPassword },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return resp.data;
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, requestMagicLink, confirmMagicLink, logout, resetPassword, updateProfile, changePassword, setUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
